import { useState, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { apiService } from "../services/api";
import { AuthService } from "../services/auth";
import { ApiError, CreateShareRequest } from "../types/api";

interface UseCreateShareReturn {
  createShareLink: (request: CreateShareRequest) => Promise<string | null>;
  isLoading: boolean;
  error: ApiError | null;
}

// Returns the full, shareable URL (origin + /s/{token}) on success.
export function useCreateShare(): UseCreateShareReturn {
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);

  const createShareLink = useCallback(
    async (request: CreateShareRequest): Promise<string | null> => {
      const idToken = AuthService.getIdToken(auth);
      if (!idToken) {
        setError({ message: "User is not authenticated" });
        return null;
      }

      try {
        setIsLoading(true);
        setError(null);

        const { token } = await apiService.createShare(request, idToken);
        return `${window.location.origin}/s/${token}`;
      } catch (err) {
        setError({
          message:
            err instanceof Error ? err.message : "Failed to create share link",
          status: (err as ApiError)?.status,
        });
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [auth],
  );

  return { createShareLink, isLoading, error };
}
